"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APPROVED_COMPANY_REPORTED_FLAG = exports.BANNED_VISIBLE_SUBSTRINGS = void 0;
exports.mapCredibilityFlag = mapCredibilityFlag;
exports.containsBannedVisibleCopy = containsBannedVisibleCopy;
exports.assertsVerification = assertsVerification;
exports.toApprovedVisibleLabel = toApprovedVisibleLabel;
const types_1 = require("@repo/types");
// CRE-987 — visible-copy gate for model-generated labels.
//
// Locked rules: "Dubious" and "Unattributed" never appear in user-facing or
// crawlable text, and claimed data never gets verification framing
// ("Verified …", "Fact-checked …", "Factual reporting", "Disputed", …).
//
// credibilityFlag is open, model-generated free text (tens of thousands of
// distinct values in prod), so this cannot be a denylist of known strings. It
// reuses the canonical CYA_PATTERN so it can never disagree with the
// deploy-blocking CI gate. A flag that trips it collapses to the caller's
// neutral fallback, never to a different provenance claim we did not
// establish ("Verify Claims" must NOT become "Company-reported").
//
// This lives in a package, not an app, because the PR-time CYA grep gate scans
// added lines under apps/extension-webapp/** and the gate has to name the
// words it blocks. Exact approved strings are owned by CRE-959.
exports.BANNED_VISIBLE_SUBSTRINGS = ['Dubious', 'Unattributed'];
// CYA_PATTERN only flags "verified reporting/claim/fact" and "fact[- ]check",
// so it misses bare framing like "Verified Trial Data" or "Independently
// Verified". Over-matching reader advice ("Verify Details") is safe because
// the fallback is neutral.
const VERIFICATION_FAMILY_RE = /\b(verified|verify|verifying|verification|fact[\s-]?check(?:ed|ing)?)\b/i;
function tripsVisibleCopyGate(value) {
    return (types_1.CYA_PATTERN.test(value) ||
        VERIFICATION_FAMILY_RE.test(value) ||
        containsBannedVisibleCopy(value));
}
/** The one exact, ticket-sanctioned reframe; never used as a fallback. */
exports.APPROVED_COMPANY_REPORTED_FLAG = 'Company-reported';
const CREDIBILITY_FLAG_OVERRIDES = {
    'Verified Trial Data': exports.APPROVED_COMPANY_REPORTED_FLAG,
};
/**
 * Map a raw `credibilityFlag` to a safe visible label.
 * - Empty/absent, or anything that trips the gate -> `null`, so the caller
 *   renders its own neutral default (e.g. "Consider") or hides the line.
 * - The exact sanctioned override -> its approved reframe.
 * - Everything else passes through trimmed (the common case).
 */
function mapCredibilityFlag(flag) {
    const trimmed = flag?.trim();
    if (!trimmed)
        return null;
    const override = CREDIBILITY_FLAG_OVERRIDES[trimmed];
    if (override)
        return override;
    if (tripsVisibleCopyGate(trimmed))
        return null;
    return trimmed;
}
/** True if `value` contains any banned visible substring (case-insensitive). */
function containsBannedVisibleCopy(value) {
    const lower = value.toLowerCase();
    return exports.BANNED_VISIBLE_SUBSTRINGS.some((b) => lower.includes(b.toLowerCase()));
}
/** True if `value` is verdict-forming / verification-framed. */
function assertsVerification(value) {
    return tripsVisibleCopyGate(value);
}
/**
 * Last-step guard for labels whose value set is not fully enumerated yet:
 * returns `fallback` for anything that trips the gate.
 */
function toApprovedVisibleLabel(value, fallback = 'Consider') {
    const trimmed = value?.trim();
    if (!trimmed || tripsVisibleCopyGate(trimmed))
        return fallback;
    return trimmed;
}
